import { PUBLIC_SITE } from '$env/static/public';
import { localizeHref, type Locale } from './locale';
import type { LocalizedMessage, MessageKey, MessageScope } from './messageTypes';

type Catalog = Record<string, string>;
type Parameters = Record<string, string | number>;
const catalogs: Record<string, { default: Catalog }> = {
	...import.meta.glob('./en/**/*.json', { eager: true }),
	...(PUBLIC_SITE === 'aqueduct'
		? import.meta.glob(['./ja/**/*.json', '!./ja/nodes.json'], { eager: true })
		: {})
};
function renderMessage(
	language: Locale,
	scope: string,
	key: string,
	values: Parameters = {}
): string {
	const catalog: Catalog | undefined = catalogs[`./${language}/${scope}.json`]?.default;
	const source: string | undefined = catalog?.[key];
	if (typeof source !== 'string' || !source.trim())
		throw new Error(`Missing localization: ${language}.${scope}.${key}`);
	return source.replace(/\{([A-Za-z][A-Za-z0-9_]*)\}/g, (_match: string, name: string): string => {
		if (!(name in values))
			throw new Error(`Missing localization parameter: ${scope}.${key}.${name}`);
		return String(values[name]);
	});
}
function escapeHtml(value: string | number): string {
	return String(value).replace(
		/[&<>"']/g,
		(character: string): string =>
			({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]!
	);
}
/** Catalog markup is checked at build time; values are always escaped. */
function renderRichMessage(
	language: Locale,
	scope: string,
	key: string,
	values: Parameters = {}
): string {
	const escaped: Parameters = Object.fromEntries(
		Object.entries(values).map(([name, value]: [string, string | number]) => [
			name,
			escapeHtml(value)
		])
	);
	return renderMessage(language, scope, key, escaped).replace(
		/href="(\/[^"<>]*)"/g,
		(_match: string, href: string): string => `href="${localizeHref(language, href)}"`
	);
}

export const message: LocalizedMessage = renderMessage;
export const richMessage: LocalizedMessage = renderRichMessage;

/** Validate externally supplied keys before passing them to the typed message API. */
export function requireMessageKey<S extends MessageScope>(scope: S, key: string): MessageKey<S> {
	if (!Object.hasOwn(catalogs[`./en/${scope}.json`].default, key))
		throw new Error(`Unknown localization key: ${scope}.${key}`);
	return key as MessageKey<S>;
}
